/**
 * Resizable admin table columns — pairwise drag at column boundaries.
 *
 * Stores all column widths as one JSON object in `localStorage` under a
 * caller-supplied key. Dragging the boundary between columns `i` and `i+1`
 * transfers width from the right column to the left (positive pointer delta)
 * while respecting per-column min/max bounds.
 *
 * Uses Pointer Events with `setPointerCapture` so drag moves are not lost over
 * sticky headers or narrow grips (same behavior as Scheduled Items).
 */

import { useCallback, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Configuration for {@link useResizableColumnWidths}.
 */
export interface ResizableColumnWidthsOptions<T extends string> {
  /** `localStorage` key for the JSON width map. */
  storageKey: string;

  /** Column identifiers in visual order (left → right). */
  columnKeys: readonly T[];

  defaultWidths: Record<T, number>;
  minWidths: Record<T, number>;
  maxWidths: Record<T, number>;

  /**
   * Optional initializer — use for migrations (e.g. legacy single-column key).
   * When omitted, widths are read via {@link readColumnWidthsFromStorage}.
   */
  readInitialWidths?: () => Record<T, number>;
}

/**
 * Merges stored JSON into defaults with per-key clamping. Safe on corrupt data.
 */
export function readColumnWidthsFromStorage<T extends string>(
  storageKey: string,
  columnKeys: readonly T[],
  defaultWidths: Record<T, number>,
  minWidths: Record<T, number>,
  maxWidths: Record<T, number>
): Record<T, number> {
  const out: Record<T, number> = { ...defaultWidths };

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return out;
    }

    const parsed = JSON.parse(raw) as Partial<Record<T, number>>;
    for (const key of columnKeys) {
      const v = parsed[key];
      if (typeof v === 'number' && Number.isFinite(v)) {
        out[key] = Math.round(
          Math.min(maxWidths[key], Math.max(minWidths[key], v))
        );
      }
    }
  } catch {
    /* ignore corrupt JSON */
  }

  return out;
}

/**
 * Hook: column width state + pointer handler for boundary `i` (between
 * `columnKeys[i]` and `columnKeys[i+1]`).
 */
export function useResizableColumnWidths<T extends string>(
  options: ResizableColumnWidthsOptions<T>
): {
  columnWidths: Record<T, number>;
  handleBoundaryPointerDown: (
    boundaryIndex: number,
    event: ReactPointerEvent<HTMLDivElement>
  ) => void;
} {
  const {
    storageKey,
    columnKeys,
    defaultWidths,
    minWidths,
    maxWidths,
    readInitialWidths,
  } = options;

  const [columnWidths, setColumnWidths] = useState<Record<T, number>>(() =>
    readInitialWidths?.() ??
    readColumnWidthsFromStorage(
      storageKey,
      columnKeys,
      defaultWidths,
      minWidths,
      maxWidths
    )
  );

  const handleBoundaryPointerDown = useCallback(
    (boundaryIndex: number, event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const leftKey = columnKeys[boundaryIndex];
      const rightKey = columnKeys[boundaryIndex + 1];

      const handleEl = event.currentTarget;
      const pointerId = event.pointerId;
      const startX = event.clientX;

      const widthsAtDragStart = { ...columnWidths };
      const startLeft = widthsAtDragStart[leftKey];
      const startRight = widthsAtDragStart[rightKey];

      const deltaMin = Math.max(
        minWidths[leftKey] - startLeft,
        startRight - maxWidths[rightKey]
      );
      const deltaMax = Math.min(
        maxWidths[leftKey] - startLeft,
        startRight - minWidths[rightKey]
      );

      handleEl.setPointerCapture(pointerId);

      let resizeEnded = false;

      let lastCommitted: Record<T, number> = { ...widthsAtDragStart };

      const onMove = (ev: globalThis.PointerEvent) => {
        if (ev.pointerId !== pointerId) {
          return;
        }

        const rawDelta = ev.clientX - startX;
        const delta = Math.min(deltaMax, Math.max(deltaMin, rawDelta));

        lastCommitted = {
          ...widthsAtDragStart,
          [leftKey]: Math.round(startLeft + delta),
          [rightKey]: Math.round(startRight - delta),
        };

        setColumnWidths(lastCommitted);
      };

      const onEnd = (ev: globalThis.PointerEvent) => {
        if (ev.pointerId !== pointerId || resizeEnded) {
          return;
        }

        resizeEnded = true;

        handleEl.removeEventListener('pointermove', onMove);
        handleEl.removeEventListener('pointerup', onEnd);
        handleEl.removeEventListener('pointercancel', onEnd);

        try {
          handleEl.releasePointerCapture(pointerId);
        } catch {
          /* already released */
        }

        try {
          localStorage.setItem(storageKey, JSON.stringify(lastCommitted));
        } catch {
          /* quota / private mode */
        }
      };

      handleEl.addEventListener('pointermove', onMove);
      handleEl.addEventListener('pointerup', onEnd);
      handleEl.addEventListener('pointercancel', onEnd);
    },
    [columnWidths, storageKey, columnKeys, minWidths, maxWidths]
  );

  return { columnWidths, handleBoundaryPointerDown };
}
